import React, { useMemo, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight,
  Search,
  ShoppingBag,
  Star,
  Zap,
  Sparkles,
  Clock,
  ShieldCheck,
  Truck,
  TrendingUp,
  ChevronRight,
  BookOpen
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useCatalog, useFeatured } from '../hooks/useCatalog';
import ElegantNavbar from '../components/layout/ElegantNavbar';
import CartSidebar from '../components/cart/CartSidebar';
import '../styles/home-elegant.css';

const categoryOptions = [
  { id: 'libro', label: 'Libros', icon: <BookOpen size={16} /> },
  { id: 'manga', label: 'Manga', icon: <Zap size={16} /> },
  { id: 'comic', label: 'Comics', icon: <Sparkles size={16} /> }
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1 }
};

const HomePage = () => {
  const [activeCategory, setActiveCategory] = useState('libro');
  const [search, setSearch] = useState('');
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const { addItem, formatPrice } = useCart();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const filters = useMemo(() => ({
    category: activeCategory,
    q: search || undefined,
    sort: 'recent'
  }), [activeCategory, search]);

  const { books, loading } = useCatalog(filters);
  const { featured } = useFeatured();

  const bestSellers = featured.slice(0, 4);
  const novedades = books.slice(0, 8);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 100,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="homepage-elegant">
      <ElegantNavbar onNavigateSection={scrollToSection} transparent={!scrolled} />

      {/* Premium Hero Section */}
      <section className="rdp-hero-premium" id="inicio">
        <div className="rdp-hero-bg">
          <div className="rdp-hero-overlay" />
        </div>

        <div className="rdp-shell">
          <div className="rdp-hero-content">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <div className="rdp-badge-pill">
                <Sparkles size={14} className="text-amber-400" />
                Nueva Colección 2024
              </div>
              <h1 className="rdp-display-title">
                Historias que <br />
                <span className="rdp-text-gradient">transforman</span> mundos
              </h1>
              <p className="rdp-hero-lead">
                Explora una curaduría excepcional de literatura, manga y cómic.
                Diseñado para los amantes del papel y la narrativa visual.
              </p>

              <div className="rdp-hero-actions-premium">
                <button
                  className="rdp-btn-primary-lg"
                  onClick={() => scrollToSection('catalogo')}
                >
                  Explorar Catálogo
                  <ChevronRight size={18} />
                </button>
                <button
                  className="rdp-btn-glass-lg"
                  onClick={() => navigate('/books')}
                >
                  Ver Novedades
                </button>
              </div>

              <div className="rdp-trust-badges">
                <div className="rdp-trust-item">
                  <ShieldCheck size={20} />
                  <span>Compra Segura</span>
                </div>
                <div className="rdp-trust-item">
                  <Truck size={20} />
                  <span>Envío Global</span>
                </div>
                <div className="rdp-trust-item">
                  <Clock size={20} />
                  <span>Soporte 24/7</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="rdp-hero-visual"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.2 }}
            >
              <div className="rdp-glass-card-featured">
                <div className="rdp-glass-header">
                  <TrendingUp size={16} className="text-emerald-400" />
                  <span>Tendencias de la semana</span>
                </div>
                <div className="rdp-featured-list">
                  {bestSellers.length > 0 ? bestSellers.map((book, i) => (
                    <div key={book.id} className="rdp-featured-mini-item">
                      <div className="rdp-mini-num">0{i + 1}</div>
                      <div className="rdp-mini-info">
                        <strong>{book.title}</strong>
                        <span>{book.author}</span>
                      </div>
                    </div>
                  )) : (
                    Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className="rdp-featured-mini-item skeleton">
                        <div className="rdp-mini-num-skeleton" />
                        <div className="rdp-mini-info-skeleton" />
                      </div>
                    ))
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <div className="rdp-shell">
        {/* Search & Categories */}
        <section className="rdp-discovery-bar" id="catalogo">
          <div className="rdp-bar-inner">
            <div className="rdp-search-box">
              <Search className="rdp-search-icon" size={20} />
              <input
                type="text"
                placeholder="Busca por título, autor o género..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="rdp-category-tabs">
              {categoryOptions.map((cat) => (
                <button
                  key={cat.id}
                  className={`rdp-cat-tab ${activeCategory === cat.id ? 'is-active' : ''}`}
                  onClick={() => setActiveCategory(cat.id)}
                >
                  {cat.icon}
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Products Grid */}
        <section className="rdp-products-section">
          <div className="rdp-section-meta">
            <div className="rdp-label">Nuestros títulos</div>
            <h2 className="rdp-section-title-premium">Explora las Novedades</h2>
          </div>

          <motion.div
            className="rdp-premium-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            {loading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="rdp-product-skeleton" />
              ))
            ) : novedades.length > 0 ? (
              novedades.map((product) => (
                <motion.article
                  key={product.id}
                  className="rdp-premium-card"
                  variants={itemVariants}
                  whileHover={{ y: -10 }}
                >
                  <div className="rdp-pcard-cover">
                    <img src={product.coverUrl} alt={product.title} />
                    <div className="rdp-pcard-overlay">
                      <button
                        className="rdp-quick-add"
                        onClick={() => addItem(product)}
                      >
                        <ShoppingBag size={20} />
                      </button>
                    </div>
                  </div>
                  <div className="rdp-pcard-content">
                    <div className="rdp-pcard-top">
                      <span className="rdp-pcard-cat">{product.category}</span>
                      <div className="rdp-pcard-rating">
                        <Star size={12} fill="currentColor" />
                        {product.rating.toFixed(1)}
                      </div>
                    </div>
                    <h3 className="rdp-pcard-title">{product.title}</h3>
                    <p className="rdp-pcard-author">{product.author}</p>
                    <div className="rdp-pcard-footer">
                      <span className="rdp-pcard-price">{formatPrice(product.price)}</span>
                      <button
                        className="rdp-pcard-link"
                        onClick={() => navigate(`/libro/${product.id}`)}
                      >
                        Detalles
                        <ArrowRight size={14} />
                      </button>
                    </div>
                  </div>
                </motion.article>
              ))
            ) : (
              <div className="rdp-no-results">
                <p>No se encontraron resultados para tu búsqueda.</p>
                <button onClick={() => setSearch('')}>Limpiar búsqueda</button>
              </div>
            )}
          </motion.div>
        </section>

        {/* Services Section */}
        <section className="rdp-services-banner">
          <div className="rdp-service-item">
            <div className="rdp-service-icon"><Truck /></div>
            <div className="rdp-service-text">
              <h4>Envío Premium</h4>
              <p>Seguimiento en tiempo real y empaque protegido.</p>
            </div>
          </div>
          <div className="rdp-service-item">
            <div className="rdp-service-icon"><ShieldCheck /></div>
            <div className="rdp-service-text">
              <h4>Garantía Total</h4>
              <p>30 días para cambios sin preguntas.</p>
            </div>
          </div>
          <div className="rdp-service-item">
            <div className="rdp-service-icon"><Sparkles /></div>
            <div className="rdp-service-text">
              <h4>Ediciones Especiales</h4>
              <p>Acceso exclusivo a preventas y firmas.</p>
            </div>
          </div>
        </section>
      </div>

      <CartSidebar />

      <footer className="rdp-footer-premium">
        <div className="rdp-shell">
          <div className="rdp-footer-content">
            <div className="rdp-footer-brand">
              <span className="rdp-f-logo">Relatos de Papel</span>
              <p>La librería digital pensada para el lector moderno. Calidad, diseño y curaduría en cada página.</p>
            </div>
            <div className="rdp-footer-links">
              <div className="rdp-f-col">
                <h5>Explorar</h5>
                <a>Catálogo</a>
                <a>Novedades</a>
                <a>Bestsellers</a>
              </div>
              <div className="rdp-f-col">
                <h5>Ayuda</h5>
                <a>Envíos</a>
                <a>Devoluciones</a>
                <a>Contacto</a>
              </div>
            </div>
          </div>
          <div className="rdp-footer-bottom">
            <p>© 2024 Relatos de Papel. Desarrollado por Pablo Matute.</p>
            <div className="rdp-f-legal">
              <a>Privacidad</a>
              <a>Términos</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
